import {DialogsEnum} from "@/lib/enums/DialogsEnum";
import {DialogConfig, DialogLayer} from "./types";

/**
 * Single source of truth for dialog metadata.
 *
 * The registry is intentionally dumb: it stores configs and applies sane
 * defaults. All decision logic lives in the policy engine / orchestrator so the
 * registry stays trivially testable and serialisable.
 */
export class DialogRegistry {
    private readonly configs = new Map<DialogsEnum, DialogConfig>();

    constructor(initial?: DialogConfig[]) {
        if (initial) this.registerMany(initial);
    }

    register(config: DialogConfig): this {
        this.configs.set(config.id, withDefaults(config));
        return this;
    }

    registerMany(configs: DialogConfig[]): this {
        for (const c of configs) this.register(c);
        return this;
    }

    /** Patch an existing config (e.g. feature-flag a cooldown at runtime). */
    update(id: DialogsEnum, patch: Partial<DialogConfig>): this {
        const current = this.get(id);
        this.configs.set(id, withDefaults({...current, ...patch, id}));
        return this;
    }

    has(id: DialogsEnum): boolean {
        return this.configs.has(id);
    }

    /** Returns the config, or a defaulted fallback so callers never crash. */
    get(id: DialogsEnum): DialogConfig {
        return this.configs.get(id) ?? withDefaults({id});
    }

    all(): DialogConfig[] {
        return [...this.configs.values()];
    }
}

/** Apply defaults once at registration time so the rest of the engine is total. */
function withDefaults(config: DialogConfig): Required<Pick<DialogConfig,
    "id" | "mode" | "layer" | "category" | "blocking" | "persistent" |
    "requiresAuth" | "dedupe" | "maxDisplaysScope">> & DialogConfig {
    return {
        mode: "queue",
        layer: DialogLayer.CONTENT,
        category: "utility",
        blocking: false,
        persistent: false,
        requiresAuth: false,
        dedupe: true,
        maxDisplaysScope: "session",
        ...config,
    };
}
